import dayjs from "dayjs";
import _ from "lodash";
import type { TechnologyItem } from "app/pages/workspace/sales/sub-modules/feed/types/technologies";
import { StatusFilterValue } from "./types";

export function enrichWithStatusAndPeriods(technologyList: TechnologyItem[]): TechnologyItem[] {
    return technologyList.map((item) => {
        const { usages } = item;
        const status = usages.every((usage) => !!usage.removalDate) ? "removed" : "installed";

        const installationDates = usages.map((u) => new Date(u.installationDate).getTime());
        const earliestInstallationDate = Math.min(...installationDates);

        const removalDates = usages.filter((u) => u.removalDate).map((u) => new Date(u.removalDate).getTime());

        const latestRemovalDate = Math.max(...removalDates);

        const workingPeriod =
            status === "installed"
                ? dayjs().diff(earliestInstallationDate)
                : dayjs(latestRemovalDate).diff(earliestInstallationDate);

        const removedPeriod = status === "removed" ? dayjs().diff(latestRemovalDate) : 0;

        return {
            ...item,
            status,
            installationDate: earliestInstallationDate,
            workingPeriod,
            removedPeriod,
        };
    });
}

export const filterByStatus =
    (statusFilterValue: StatusFilterValue) =>
    (technologyList: TechnologyItem[]): TechnologyItem[] => {
        if (statusFilterValue === StatusFilterValue.InstalledAndRemoved) {
            return technologyList;
        }

        return technologyList.filter((tech) => {
            return statusFilterValue === StatusFilterValue.Installed
                ? tech.status === "installed"
                : tech.status === "removed";
        });
    };

export function enrichWithIsUsedInDomain(technologyList: TechnologyItem[], domains: string[]): TechnologyItem[] {
    return technologyList.map((tech) => {
        const isUsedInDomainPairs = domains.map((domain) => {
            const isUsed = tech?.usedBy.some((subdomain) => subdomain.includes(domain));
            return [domain, isUsed] as [string, boolean];
        });

        return { ...tech, ..._.fromPairs(isUsedInDomainPairs) };
    });
}

export function getSortedSubdomainCounts(enrichedTechnologies: TechnologyItem[]): Record<string, number> {
    return _(enrichedTechnologies)
        .filter((item) => !!item.usedBy)
        .flatMap((item) => item.usedBy)
        .countBy()
        .toPairs()
        .sortBy(0)
        .fromPairs()
        .value();
}
